import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, DropZone, InlineStack, Text, Thumbnail, Banner } from '@shopify/polaris';
import MentionPopover, { filteredItems } from './MentionPopover';
import MentionEditor from './MentionEditor';
import { MENTION_RE } from './mentions';

const MAX_IMAGES = 4;

export default function CommentComposer({ orderId, storeFetch, mirrorAs, onPosted, onCancel, prefill, initial, commentId, replyToMentionId }) {
    const [text, setText] = useState(initial?.body || '');
    const [images, setImages] = useState(initial?.images || []);     // [{id, name, thumb_url}]
    const [users, setUsers] = useState([]);
    const [contacts, setContacts] = useState([]);              // mention contacts offered on this store (2026-10-21)
    const [contactsUnavailable, setContactsUnavailable] = useState(null);
    const [q, setQ] = useState(null);
    const [active, setActive] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const editor = useRef(null);
    const lastQuery = useRef(null);

    // Reply clicks: add the token without ever discarding the draft.
    const prefillNonce = prefill ? prefill.nonce : null;
    useEffect(() => {
        if (!prefill) return;
        const m = new RegExp(MENTION_RE.source).exec(prefill.token || '');
        if (m && editor.current) editor.current.appendMention({ kind: m[1], id: m[2], name: m[3].trim() });
    }, [prefillNonce]); // eslint-disable-line

    useEffect(() => {
        storeFetch(`/api/orders/${orderId}/mentionable`).then(r => r.ok ? r.json() : { users: [] })
            .then(d => { setUsers(d.users || []); setContacts(d.contacts || []); setContactsUnavailable(d.contacts_unavailable || null); }).catch(() => {});
    }, [orderId]); // eslint-disable-line

    const candidates = q && !dismissed ? filteredItems(users, contacts, q.query) : [];
    const showNote = !!(q && !dismissed && contactsUnavailable && q.query);

    const pick = useCallback((u) => {
        setActive(0);
        if (editor.current) editor.current.insertMention(u);
    }, []);

    const onKeyDown = (e) => {
        if (!candidates.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a + 1) % candidates.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a - 1 + candidates.length) % candidates.length); }
        else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(candidates[active] || candidates[0]); }
        else if (e.key === 'Escape') { setDismissed(true); setActive(0); }
    };

    const onDrop = useCallback(async (_all, accepted) => {
        setError(null);
        for (const f of accepted.slice(0, MAX_IMAGES - images.length)) {
            const fd = new FormData(); fd.append('file', f);
            const r = await storeFetch('/api/comment-images', { method: 'POST', body: fd }, false);
            if (!r.ok) { setError((await r.json().catch(() => ({}))).detail || 'Upload failed'); continue; }
            const meta = await r.json();
            setImages(prev => [...prev, { id: meta.id, name: meta.name, thumb_url: `/api/comment-images/${meta.id}?thumb=1` }]);
        }
    }, [images.length, storeFetch]);

    const submit = async () => {
        if (!text.trim() || busy) return;
        setBusy(true); setError(null);
        try {
            const body = { body: text.trim(), image_ids: images.map(i => i.id), reply_to_mention_id: replyToMentionId || null };
            const r = await storeFetch(commentId ? `/api/comments/${commentId}` : `/api/orders/${orderId}/comments`,
                { method: commentId ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) { setError(d.detail || 'Could not post comment'); return; }
            setText(''); setImages([]); if (editor.current) editor.current.clear(); onPosted && onPosted(d.comment);
        } finally { setBusy(false); }
    };

    return (
        <div className="cm-composer">
            {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}
            <div className="cm-composer__field">
                <MentionEditor value={text} editorRef={editor} maxLength={4000}
                    placeholder="Leave a comment… use @ to mention someone who can see this order"
                    onChange={(v) => { setText(v); setDismissed(false); }}
                    onQueryChange={(qi) => {
                        const prevQ = lastQuery.current; lastQuery.current = qi ? qi.query : null;
                        if (prevQ !== lastQuery.current) setActive(0);
                        setQ(prev => (prev && qi && prev.query === qi.query) ? prev : qi);
                    }}
                    onKeyDown={onKeyDown} />
                {(candidates.length > 0 || showNote) && <MentionPopover items={candidates} unavailable={showNote ? contactsUnavailable : null} activeIndex={active} onPick={pick} />}
            </div>
            {images.length > 0 && (
                <InlineStack gap="200">
                    {images.map(im => (
                        <div key={im.id} className="cm-thumb">
                            <Thumbnail source={im.thumb_url || im.url} alt={im.name || ''} size="small" />
                            <button type="button" className="cm-thumb__x" aria-label="Remove image"
                                onClick={() => setImages(prev => prev.filter(x => x.id !== im.id))}>×</button>
                        </div>
                    ))}
                </InlineStack>
            )}
            <InlineStack align="space-between" blockAlign="center" gap="200" wrap>
                <InlineStack gap="200" blockAlign="center">
                    {images.length < MAX_IMAGES && (
                        <div className="cm-dropzone">
                            <DropZone accept="image/png,image/jpeg,image/webp" type="image" allowMultiple onDrop={onDrop} outline={false} variableHeight>
                                <Button size="slim">Add image</Button>
                            </DropZone>
                        </div>
                    )}
                    {mirrorAs !== undefined && (
                        <Text as="span" tone="subdued" variant="bodySm">
                            {mirrorAs ? `Will also post to Shopify as ${mirrorAs}` : 'OpsHub only'}
                        </Text>
                    )}
                </InlineStack>
                <InlineStack gap="200">
                    {onCancel && <Button size="slim" onClick={onCancel}>Cancel</Button>}
                    <Button size="slim" variant="primary" loading={busy} disabled={!text.trim()} onClick={submit}>
                        {commentId ? 'Save' : replyToMentionId ? 'Reply' : 'Post comment'}
                    </Button>
                </InlineStack>
            </InlineStack>
        </div>
    );
}
